import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then(() => setState("valid"))
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setState("done");
      toast.success("Password reset — you can now sign in.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-primary text-primary-foreground grain overflow-hidden flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2 relative z-10"><Crown className="w-7 h-7 text-accent" /><span className="font-display font-extrabold text-xl tracking-tight">GALAHADJI</span></Link>
        <div className="relative z-10">
          <h2 className="font-marketing text-4xl font-bold leading-tight">Set a new<br /><span className="italic text-accent">password.</span></h2>
          <p className="text-white/60 mt-4 max-w-md">Choose a strong password to secure your Galahadji Acquisition OS account.</p>
        </div>
        <div className="relative z-10 text-white/40 text-xs uppercase tracking-[0.2em]">Secure Team Access</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm" data-testid="reset-password-page">
          <div className="flex items-center gap-2 mb-8 lg:hidden"><Crown className="w-6 h-6 text-accent" /><span className="font-display font-extrabold text-lg">GALAHADJI</span></div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2"><KeyRound className="w-3.5 h-3.5" /> Password Reset</div>

          {state === "checking" && <p className="text-sm text-muted-foreground mt-6" data-testid="reset-checking">Verifying your reset link…</p>}

          {state === "invalid" && (
            <div className="mt-2" data-testid="reset-invalid">
              <h1 className="font-display text-3xl font-bold tracking-tight mb-3">Link expired</h1>
              <p className="text-sm text-muted-foreground mb-6">This reset link is invalid or has expired. Reset links can be used once and last 1 hour. Please request a new one.</p>
              <Button className="w-full h-11" onClick={() => nav("/login")} data-testid="reset-request-new">Back to sign in</Button>
            </div>
          )}

          {state === "valid" && (
            <form onSubmit={submit} className="mt-2" data-testid="reset-form">
              <h1 className="font-display text-3xl font-bold tracking-tight mb-3">Choose a new password</h1>
              <p className="text-sm text-muted-foreground mb-6">Must be at least 12 characters with an uppercase letter, a lowercase letter, and a number.</p>
              {error && <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-4 py-3 mb-4" data-testid="reset-error">{error}</div>}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New password</Label>
                  <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-password" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirm password</Label>
                  <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-password" required />
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full mt-6 h-11" data-testid="reset-submit">{busy ? "Saving…" : "Reset password"}</Button>
            </form>
          )}

          {state === "done" && (
            <div className="mt-2" data-testid="reset-done">
              <div className="flex items-center gap-2 text-accent mb-3"><CheckCircle2 className="w-6 h-6" /><h1 className="font-display text-3xl font-bold tracking-tight">All set</h1></div>
              <p className="text-sm text-muted-foreground mb-6">Your password has been reset. Sign in with your new password to continue.</p>
              <Button className="w-full h-11" onClick={() => nav("/login")} data-testid="reset-goto-login">Go to sign in</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
